"use client"

import { useTranslations } from "next-intl"
import { useRef, useState } from "react"
import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { TIconButton } from "../../ui/button/TIconButton"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"

interface FileFieldProps {
	field: ParsedField
	accept?: string
	onUpload?: (file: File) => Promise<unknown>
}

export function FileField({ field: parsedField, accept, onUpload }: FileFieldProps) {
	const { name, required } = parsedField
	const description = parsedField.schema.description
	const title = parsedField.schema.title
	const { control } = useFormContext()
	const t = useTranslations()
	const inputRef = useRef<HTMLInputElement>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [isUploading, setIsUploading] = useState(false)

	return (
		<Controller
			name={name as FieldPath<FieldValues>}
			control={control}
			render={({ field }) => {
				const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
					const file = e.target.files?.[0]
					if (!file) return

					// onUpload 함수가 제공되면 업로드 후 결과 저장
					if (onUpload) {
						try {
							setIsUploading(true)
							const uploadResult = await onUpload(file)
							field.onChange(uploadResult)
						} catch (error) {
							console.error("Upload failed:", error)
							// 업로드 실패 시 File 객체 그대로 저장 (fallback)
							field.onChange(file)
						} finally {
							setIsUploading(false)
						}
					} else {
						// onUpload가 없으면 File 객체를 직접 필드 값으로 설정
						field.onChange(file)
					}
				}

				const handleUploadClick = (e: React.MouseEvent) => {
					e.stopPropagation()
					fileInputRef.current?.click()
				}

				const handleFormItemClick = () => {
					inputRef.current?.focus()
				}

				// File 객체인지 UploadResult인지 URL 문자열인지 판별
				const isFile = field.value && typeof field.value === "object" && "name" in field.value
				const isUploadResult = field.value && typeof field.value === "object" && "nodeId" in field.value
				const uploadResult = isUploadResult ? (field.value as { originalFileName?: string }) : null
				const displayName = isFile ? (field.value as File).name : isUploadResult ? uploadResult?.originalFileName || "Uploaded file" : (field.value ?? "")

				return (
					<FormItem
						className="bg-g800/10 rounded-lg px-3 py-2 flex col group transition-colors cursor-text hover:bg-g800/20 focus-within:bg-g800/20"
						onClick={handleFormItemClick}
					>
						<input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="hidden" disabled={isUploading} />
						<div className="flex flex-1 items-center gap-2">
							<div className="flex-1 flex flex-col gap-0.5 justify-center">
								<DynamicFormLabel name={name} title={title} required={required} description={description} />
								<FormControl>
									<input
										ref={inputRef}
										type="text"
										name={name}
										value={displayName}
										onChange={(e) => field.onChange(e.target.value)}
										onBlur={field.onBlur}
										onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
										placeholder={
											isUploading
												? t("creagen-components.fields.file-field.placeholder.uploading")
												: t("creagen-components.fields.file-field.placeholder.uploadOrEnterUrl")
										}
										autoComplete="off"
										disabled={isUploading}
										className="w-full bg-transparent outline-none text-white placeholder:text-text-placeholder disabled:opacity-50"
									/>
								</FormControl>
							</div>
							<TIconButton name="upload" size="small" color="ghost" onClick={handleUploadClick} className="self-center" disabled={isUploading} />
						</div>
						<FormMessage />
					</FormItem>
				)
			}}
		/>
	)
}
